import { Reducer, useCallback, useReducer } from 'react';

import { ApiStatus, API_STATUS } from '@/constants';

import {
  createViewApi,
  CreateViewApiResponse,
  isCreateViewApiResultCode,
  CREATE_VIEW_API_RESULT_CODE,
} from '../createViewApi';

/**
 * State
 */
export type CreateViewState = {
  // レスポンスデータ
  data?: CreateViewApiResponse;
  // APIの呼び出し状態
  status: ApiStatus;
};

/**
 * Actionタイプ
 */
const CREATE_VIEW_ACTION_TYPE = {
  SET_CREATE_VIEW: 'SET_CREATE_VIEW',
  SET_CREATE_VIEW_SUCCESS: 'SET_CREATE_VIEW_SUCCESS',
  SET_CREATE_VIEW_FAILED: 'SET_CREATE_VIEW_FAILED',
  SET_CREATE_VIEW_IDLE: 'SET_CREATE_VIEW_IDLE',
} as const;

/**
 * Action
 */
type CreateViewAction = {
  data?: CreateViewApiResponse;
  type: keyof typeof CREATE_VIEW_ACTION_TYPE;
};

/**
 * reducer関数
 *
 * @param state
 * @param action
 * @returns
 */
const reducer: Reducer<CreateViewState, CreateViewAction> = (
  state: CreateViewState,
  action: CreateViewAction,
) => {
  switch (action.type) {
    case CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW:
      return {
        ...state,
        status: API_STATUS.LOADING,
      };

    case CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_SUCCESS:
      return {
        ...state,
        data: action.data,
        status: API_STATUS.SUCCESS,
      };

    case CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_FAILED:
      return {
        ...state,
        data: action.data,
        status: API_STATUS.FAILED,
      };

    case CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_IDLE:
      return {
        ...state,
        status: API_STATUS.IDLE,
      };
    default:
      return state;
  }
};

/**
 * 共有画面作成API 呼び出しhooks
 *
 * @returns
 */
export const useCreateViewApi = () => {
  const [state, dispatch] = useReducer(reducer, {
    data: undefined,
    status: API_STATUS.IDLE,
  });

  /**
   * API呼び出し
   */
  const createView = useCallback(() => {
    if (state.status === API_STATUS.LOADING) {
      return;
    }

    dispatch({ type: CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW });

    createViewApi()
      .then((response: CreateViewApiResponse) => {
        // 成功
        if (response.resultCode === CREATE_VIEW_API_RESULT_CODE.OK) {
          dispatch({
            type: CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_SUCCESS,
            data: response,
          });

          return;
        }

        // 意図しない結果コードの型だった場合は失敗とする
        if (!isCreateViewApiResultCode(response.resultCode)) {
          dispatch({
            type: CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_FAILED,
            data: {
              ...response,
              resultCode: CREATE_VIEW_API_RESULT_CODE.ERR_UNKNOWN,
            } as CreateViewApiResponse,
          });

          return;
        }

        // 失敗
        dispatch({
          type: CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_FAILED,
          data: response,
        });
      })
      .catch((_) => {
        dispatch({
          type: CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_FAILED,
          data: {
            resultCode: CREATE_VIEW_API_RESULT_CODE.ERR_UNKNOWN,
          } as CreateViewApiResponse,
        });
      });
  }, [state.status]);

  /**
   * APIのステータスをリセットする
   */
  const resetCreateViewState = useCallback(() => {
    dispatch({ type: CREATE_VIEW_ACTION_TYPE.SET_CREATE_VIEW_IDLE });
  }, []);

  return {
    createViewState: state,
    createView,
    resetCreateViewState,
  };
};
